import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { pdApi, type PdOpportunity } from '../api';
import { HelpPanel } from '../HelpPanel';
import { SCORING_COMPLETED } from '../helpContent';
import './ScoringQueue.css';

/**
 * Opportunities → Completed Scoring.
 *
 * CLONED from `/Users/glucic/src/ai_readiness_template/views/opportunities-scoring-completed.ejs`
 * (660 lines) — its own view file. Shares `ScoringQueue.css` because all three source views
 * ship the same style block; the SCREENS stay separate (§1.2.1).
 *
 * WHAT MAKES THIS SCREEN DIFFERENT:
 *   · a DOCS column — three document links, each a Material icon, greyed when absent
 *   · a SCORE column — the overall score, coloured, with an APPROVED chip beneath it
 *   · Scored Date rather than Submitted
 *   · 🔄 Re-score, which is DISABLED in two distinct cases, each with its own reason
 *   · 📊 empty state
 *
 * RE-SCORING IS REFUSED TWICE OVER, and the two reasons are not the same thing:
 *   Approved      the decision has been taken; the score is the record of it
 *   has_effort    effort-planning rows exist downstream and were built on this score
 * The source shows a 🔒 for both, with different tooltips, and that distinction is kept —
 * a disabled control that cannot say why is just a broken one.
 */

type Row = PdOpportunity & Record<string, unknown>;

interface ProjectGroup {
  projectId: string;
  description: string;
  opportunities: Row[];
}

const s = (v: unknown) => (v === null || v === undefined ? '' : String(v));
const shortDate = (v: unknown) => (v ? new Date(String(v)).toLocaleDateString() : '');

/** Source :240 — 4+ green, 3+ amber, below red. */
const scoreColour = (n: number) => (n >= 4 ? '#4CAF50' : n >= 3 ? '#FF9800' : '#F44336');

/** Source :212–231 — the three document slots, in the source's order and icons. */
const DOCS: { field: string; icon: string; title: string }[] = [
  { field: 'initial_discussion_link', icon: 'description', title: 'Initial Discussion / Observation' },
  { field: 'business_process_requirements_link', icon: 'table_chart', title: 'Business Process Requirements' },
  { field: 'technical_requirements_link', icon: 'code', title: 'Technical Requirements' },
];

export function ScoringCompleted() {
  const navigate = useNavigate();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState<Set<string>>(new Set());
  const [filterProject, setFilterProject] = useState('');
  const [filterOpportunity, setFilterOpportunity] = useState('');
  const [rootEl, setRootEl] = useState<HTMLDivElement | null>(null);
  const [rootH, setRootH] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await pdApi.scoring.completed();
      setRows((data.opportunities as Row[]) ?? []);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    if (!rootEl) return;
    const main = rootEl.closest('main');
    const measure = () => {
      const top = rootEl.getBoundingClientRect().top;
      const inset = main ? parseFloat(getComputedStyle(main).paddingBottom) || 0 : 0;
      const next = Math.max(320, Math.floor(window.innerHeight - top - inset));
      setRootH((prev) => (prev !== null && Math.abs(prev - next) < 2 ? prev : next));
    };
    measure();
    window.addEventListener('resize', measure);
    const ro = new ResizeObserver(measure);
    if (main) ro.observe(main);
    return () => {
      window.removeEventListener('resize', measure);
      ro.disconnect();
    };
  }, [rootEl]);

  const groups = useMemo<ProjectGroup[]>(() => {
    const byProject = new Map<string, ProjectGroup>();
    for (const o of rows) {
      const projectId = s(o.project_id) || 'No Project';
      let g = byProject.get(projectId);
      if (!g) {
        g = { projectId, description: s(o.project_description), opportunities: [] };
        byProject.set(projectId, g);
      }
      g.opportunities.push(o);
    }
    return [...byProject.values()].sort((a, b) => a.projectId.localeCompare(b.projectId));
  }, [rows]);

  const visible = useMemo(() => {
    const p = filterProject.trim().toLowerCase();
    const q = filterOpportunity.trim().toLowerCase();
    return groups
      .map((g) => ({
        ...g,
        opportunities: q
          ? g.opportunities.filter(
              (o) =>
                s(o.opportunity_id).toLowerCase().includes(q) ||
                s(o.description).toLowerCase().includes(q),
            )
          : g.opportunities,
      }))
      .filter((g) => (p ? g.projectId.toLowerCase().includes(p) : true))
      .filter((g) => g.opportunities.length > 0);
  }, [groups, filterProject, filterOpportunity]);

  const visibleCount = visible.reduce((n, g) => n + g.opportunities.length, 0);

  const toggle = (id: string) =>
    setOpen((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const rescore = async (id: string) => {
    if (!window.confirm(`Re-score ${id}? The existing score is cleared; the answers are kept.`))
      return;
    setBusy(true);
    setError(null);
    try {
      await pdApi.scoring.rescore(id);
      await load();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="sq-page"
      ref={setRootEl}
      style={rootH === null ? undefined : { height: `${rootH}px` }}
    >
      <div className="sq-fixed">
        <div className="sq-header">
          <div>
            <h1 className="sq-title">Completed Scoring</h1>
            <p className="sq-subtitle">Opportunities that have been scored</p>
          </div>
          <HelpPanel help={SCORING_COMPLETED} />
        </div>

        <div className="sq-quick">
          <button
            type="button"
            title="Expand All"
            onClick={() => setOpen(new Set(groups.map((g) => g.projectId)))}
          >
            ⬇️ Expand All
          </button>
          <button type="button" title="Collapse All" onClick={() => setOpen(new Set())}>
            ⬆️ Collapse All
          </button>
        </div>

        {rows.length > 0 ? (
          <div className="sq-filters">
            <div className="sq-filter">
              <label htmlFor="cs-fp">Project:</label>
              <input
                id="cs-fp"
                type="text"
                placeholder="Filter..."
                value={filterProject}
                onChange={(e) => setFilterProject(e.target.value)}
              />
            </div>
            <div className="sq-filter">
              <label htmlFor="cs-fo">Opportunity:</label>
              <input
                id="cs-fo"
                type="text"
                placeholder="Filter..."
                value={filterOpportunity}
                onChange={(e) => setFilterOpportunity(e.target.value)}
              />
            </div>
            <button
              type="button"
              className="sq-clear"
              onClick={() => {
                setFilterProject('');
                setFilterOpportunity('');
              }}
            >
              Clear
            </button>
            <span className="sq-count">
              {visibleCount} of {rows.length} opportunities
            </span>
          </div>
        ) : null}
      </div>

      <div className="sq-body">
        {error ? <div className="sq-error">{error}</div> : null}

        <div className="sq-card">
          {loading ? (
            <div className="sq-empty">
              <p>Loading…</p>
            </div>
          ) : rows.length === 0 ? (
            /* Source :122 — wording preserved exactly. */
            <div className="sq-empty">
              <div className="sq-empty-icon">📊</div>
              <h3>No Scored Opportunities Yet</h3>
              <p>Completed opportunities will appear here</p>
            </div>
          ) : (
            <>
              <div className="sq-thead-wrap">
                <table className="sq-table">
                  <thead>
                    <tr>
                      <th style={{ width: 40 }} />
                      <th style={{ width: 300 }}>Project / Opportunity</th>
                      <th style={{ width: 300 }}>Description</th>
                      <th style={{ width: 80, textAlign: 'center' }}>Docs</th>
                      <th style={{ width: 120, textAlign: 'center' }}>Owner</th>
                      <th style={{ width: 100, textAlign: 'center' }}>Score</th>
                      <th style={{ width: 120, textAlign: 'center' }}>Scored Date</th>
                      <th style={{ width: 120, textAlign: 'center' }}>Actions</th>
                    </tr>
                  </thead>
                </table>
              </div>

              <div className="sq-tbody-wrap">
                <table className="sq-table">
                  <tbody>
                    {visible.map((g) => {
                      const isOpen = open.has(g.projectId);
                      const n = g.opportunities.length;
                      return (
                        <Fragment key={g.projectId}>
                          <tr className="sq-project-row" onClick={() => toggle(g.projectId)}>
                            <td style={{ width: 40 }}>
                              <span className="sq-expand">{isOpen ? '▼' : '▶'}</span>
                            </td>
                            <td style={{ width: 300, fontSize: 12 }}>
                              📁 <strong>{g.projectId}</strong>
                            </td>
                            <td className="sq-cell-ellipsis" style={{ width: 300, fontSize: 11 }}>
                              {g.description}
                            </td>
                            <td style={{ width: 80 }} />
                            <td style={{ width: 120 }} />
                            <td style={{ width: 100 }} />
                            <td style={{ width: 120 }} />
                            <td className="sq-cell-center" style={{ width: 120, fontSize: 11 }}>
                              {n} {n === 1 ? 'Opportunity' : 'Opportunities'}
                            </td>
                          </tr>

                          {isOpen
                            ? g.opportunities.map((o) => {
                                const approved = s(o.status) === 'Approved';
                                const hasEffort = Boolean(o.has_effort);
                                const score = o.overall_score == null ? null : Number(o.overall_score);
                                return (
                                  <tr key={s(o.opportunity_id)} className="sq-opportunity-row">
                                    <td style={{ width: 40 }} />
                                    <td
                                      className="sq-cell-indent"
                                      style={{ width: 300, fontSize: 11 }}
                                    >
                                      📂 <strong>{s(o.opportunity_id)}</strong>
                                    </td>
                                    <td
                                      className="sq-cell-ellipsis"
                                      style={{ width: 300, fontSize: 11 }}
                                    >
                                      {s(o.description)}
                                    </td>

                                    {/* Docs — a live link, or a greyed icon meaning "none". */}
                                    <td className="sq-cell-center" style={{ width: 80 }}>
                                      <div className="sq-docs">
                                        {DOCS.map((d) => {
                                          const href = s(o[d.field]);
                                          return href ? (
                                            <a
                                              key={d.field}
                                              href={href}
                                              target="_blank"
                                              rel="noreferrer"
                                              title={d.title}
                                              className="sq-doc"
                                            >
                                              <i className="material-icons">{d.icon}</i>
                                            </a>
                                          ) : (
                                            <i
                                              key={d.field}
                                              className="material-icons sq-doc--none"
                                              title="No document"
                                            >
                                              {d.icon}
                                            </i>
                                          );
                                        })}
                                      </div>
                                    </td>

                                    <td
                                      className="sq-cell-center"
                                      style={{ width: 120, fontSize: 11 }}
                                    >
                                      {s(o.owner_name)}
                                    </td>

                                    <td className="sq-cell-center" style={{ width: 100 }}>
                                      <div className="sq-score">
                                        {score !== null ? (
                                          <strong
                                            style={{ fontSize: 13, color: scoreColour(score) }}
                                          >
                                            {score.toFixed(2)}
                                          </strong>
                                        ) : (
                                          <span style={{ color: '#999' }}>-</span>
                                        )}
                                        {approved ? <span className="sq-approved">APPROVED</span> : null}
                                      </div>
                                    </td>

                                    <td
                                      className="sq-cell-center sq-cell-muted"
                                      style={{ width: 120, fontSize: 11 }}
                                    >
                                      {shortDate(o.scored_date)}
                                    </td>

                                    <td className="sq-cell-center" style={{ width: 120 }}>
                                      <div className="sq-actions">
                                        <button
                                          type="button"
                                          className="sq-icon-btn"
                                          title="View the score — read only"
                                          onClick={() =>
                                            /*
                                              LANDS ON THE SCORE, not on Overview. Owner's
                                              ask, 2026-09-30: *"I can see rescore, but I
                                              need to have ability to see the scores w/o
                                              changing the scores."*

                                              The read-only view already carries a Scoring
                                              Details tab with every dimension score and
                                              every answer; from a screen that exists to list
                                              COMPLETED scores, opening anywhere else makes
                                              him hunt for it, and the only other action here
                                              is the one that destroys the score.
                                            */
                                            navigate(
                                              `/portfolio_definition/opportunities/opportunity-registry/${encodeURIComponent(s(o.opportunity_id))}?tab=scoring`,
                                            )
                                          }
                                        >
                                          👁️
                                        </button>
                                        {approved ? (
                                          <button
                                            type="button"
                                            className="sq-icon-btn sq-icon-btn--off"
                                            title="Cannot re-score — opportunity is Approved (locked)"
                                            disabled
                                          >
                                            🔒
                                          </button>
                                        ) : hasEffort ? (
                                          <button
                                            type="button"
                                            className="sq-icon-btn sq-icon-btn--off"
                                            title="Cannot re-score — effort planning records exist for this opportunity"
                                            disabled
                                          >
                                            🔒
                                          </button>
                                        ) : (
                                          <button
                                            type="button"
                                            className="sq-icon-btn"
                                            title="Re-score"
                                            disabled={busy}
                                            onClick={() => void rescore(s(o.opportunity_id))}
                                          >
                                            🔄
                                          </button>
                                        )}
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })
                            : null}
                        </Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
